import * as React from 'react';
import moment from 'moment';

import SearchMC from '../presentation/components/SearchMC';
import DropdownMC from '../presentation/components/DropdownMC';
import DateRangeMC from '../presentation/components/DateRangeMC';
import MultiSelectDropdownMC from '../presentation/components/MultiSelectDropdownMC';
import AsyncPaginatedSelectMC from '../presentation/components/AsyncPaginatedSelectMC';
import AsyncPaginatedMultiSelectMC from '../presentation/components/AsyncPaginatedMultiSelectMC';
import AmountComparisonFilterMC from '../presentation/components/AmountComparisonFilterMC';
import MultiSelectDropdownGroupMC from '../presentation/components/MultiSelectDropdownGroupMC';
import DurationComparisonInputMC from '../presentation/components/DurationComparisonInputMC';

/**
 * Gets the active start and end date values from the query params.
 * @param {object} activeQueryParams - Active search parameters.
 * @param {string} key - Main filter key.
 * @returns {{startDate: Date|null, endDate: Date|null}}
 */
const getActiveDateRangeValues = (activeQueryParams, key) => {
  let startDate = null;
  let endDate = null;

  if (activeQueryParams[key]) {
    try {
      const activeDateRange = JSON.parse(activeQueryParams[key]);
      if (activeDateRange?.startDate) {
        startDate = moment(activeDateRange.startDate).toDate();
      }
      if (activeDateRange?.endDate) {
        endDate = moment(activeDateRange.endDate).toDate();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing date range:', error);
    }
  }

  if (!startDate && activeQueryParams[`start_${key}`]) {
    startDate = moment(activeQueryParams[`start_${key}`], 'DD/MM/YYYY').toDate();
    endDate = moment(activeQueryParams[`end_${key}`], 'DD/MM/YYYY').toDate();
  }

  return { startDate, endDate };
};

/**
 * Parses duration-comparison query params (key_hours, key_minutes, key_seconds, key_comparison)
 * and returns Ransack-ready params for the given key.
 * @param {object} activeQueryParams - Active search parameters from URL.
 * @param {string} key - Filter key (e.g. 'duration').
 * @returns {object} Ransack params to merge, e.g. { 'q[duration_gteq]': 90 }. Empty if no duration set.
 */
export const parseDurationComparisonForRansack = (activeQueryParams, key) => {
  if (!activeQueryParams || !key) return {};
  const hours = parseInt(activeQueryParams[`${key}_hours`], 10) || 0;
  const minutes = parseInt(activeQueryParams[`${key}_minutes`], 10) || 0;
  const seconds = parseInt(activeQueryParams[`${key}_seconds`], 10) || 0;
  const comparison = activeQueryParams[`${key}_comparison`] || 'greater_than';
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  if (totalSeconds === 0) return {};
  const ransackPredicate = comparison === 'less_than' ? `${key}_lteq` : `${key}_gteq`;
  return { [`q[${ransackPredicate}]`]: totalSeconds };
};

export const updateQueryParamsWithoutPage = (searchParams, filter, value) => {
  const filtersSearchParams = Object.fromEntries([...searchParams]);
  delete filtersSearchParams.page;
  switch (filter.type) {
    case 'date-range': {
      const queryDatesStart = value.value?.startDate;
      const queryDatesEnd = value.value?.endDate;
      const formattedStartDate = value.value?.startDate ? `${queryDatesStart.getDate()}/${queryDatesStart.getMonth() + 1}/${queryDatesStart.getFullYear()}` : '';
      const formattedEndDate = value.value?.endDate ? `${queryDatesEnd.getDate()}/${queryDatesEnd.getMonth() + 1}/${queryDatesEnd.getFullYear()}` : '';
      filtersSearchParams[`start_${filter.key}`] = formattedStartDate;
      filtersSearchParams[`end_${filter.key}`] = formattedEndDate;
    }
      break;
    case 'amount-comparison':
      filtersSearchParams[filter.key] = value?.amount || '';
      filtersSearchParams[`${filter.key}_comparison`] = value?.comparison || 'greater_than';
      break;
    case 'duration-comparison': {
      delete filtersSearchParams[filter.key];
      const hours = value?.hours;
      const minutes = value?.minutes;
      const seconds = value?.seconds;
      const comparison = value?.comparison || 'greater_than';
      filtersSearchParams[`${filter.key}_hours`] = hours !== undefined && hours !== '' ? Number(hours) : 0;
      filtersSearchParams[`${filter.key}_minutes`] = minutes !== undefined && minutes !== '' ? Number(minutes) : 0;
      filtersSearchParams[`${filter.key}_seconds`] = seconds !== undefined && seconds !== '' ? Number(seconds) : 0;
      filtersSearchParams[`${filter.key}_comparison`] = comparison;
      break;
    }
    default:
      filtersSearchParams[filter.key] = value;
      break;
  }
  return filtersSearchParams;
};

const navigateToFiler = (
  filter,
  value,
  navigate,
  searchParams
) => {
  const filtersSearchParams = updateQueryParamsWithoutPage(searchParams, filter, value);
  navigate({
    pathname: location.pathname,
    search: `?${new URLSearchParams(filtersSearchParams).toString()}`,
    filterKey: filter.key,
    filterValue: value,
    filterType: filter.type
  });
};

export const getFilteredOptionsByComponent = (filter, activeQueryParams, asideFilters) => {
  if (!filter.dependencyKey) return filter.options;

  const selectedDependencyValue = activeQueryParams[filter.dependencyKey];
  if (!selectedDependencyValue) return [];

  const parentOption = asideFilters
    .find((f) => (f.key === filter.dependencyKey))?.options.find((opt) => String(opt.value) === String(selectedDependencyValue));

  if (!parentOption) return [];

  return parentOption[filter.parentOptionsKey]?.map((option) => ({ ...option, label: option?.name || option?.label || '' })) || [];
};

export const getFiltersComponentByType = (
  filter,
  key,
  activeQueryParams,
  navigate,
  searchParams,
  uiOptions = {}
) => {
  let component = null;
  if (!activeQueryParams) return null;
  const { useFloatingLabel = false } = uiOptions;

  const isDisabled = filter.dependencyKey && (!filter.options || filter.options.length === 0);

  const formatMultiIds = (param) => {
    if (param == null) return [];

    if (Array.isArray(param)) return param;

    if (typeof param === 'string') {
      return param
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
    }

    return [];
  };

  switch (filter.type) {
    case 'search':
      component = (
        <SearchMC
          key={key || filter.key}
          onChange={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          placeholder={filter.label}
          defaultValue={activeQueryParams[filter.key]}
          mainContainerClassName="tw-max-w-96"
          debounceTime={500}
        />
      );
      break;
    case 'select': {
      const selectOptions = (filter.options || []).map((opt) => ({
        ...opt,
        id: opt.id ?? opt.value
      }));
      component = (
        <DropdownMC
          id={`filter-select-${key || filter.key}`}
          options={selectOptions}
          key={key || filter.key}
          label={filter.label}
          search={filter.search !== undefined ? filter.search : true}
          useFloatingLabel={Boolean(filter.useFloatingLabel)}
          onSelect={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultValue={activeQueryParams[filter.key] ?? ''}
          disabled={isDisabled}
        />
      );
      break;
    }
    case 'multi-select':
      component = (
        <MultiSelectDropdownMC
          id={filter.key}
          name={filter.key}
          options={filter.options || []}
          defaultValue={formatMultiIds(activeQueryParams[filter.key])}
          onSelect={(_, value) => navigateToFiler(filter, value, navigate, searchParams)}
          placeholder={filter.placeholder || ''}
          search
          label={filter.label}
          labelTooltip={filter.labelTooltip}
          useFloatingLabel={useFloatingLabel}
        />
      );
      break;
    case 'date-range': {
      const { startDate: searchStartValue, endDate: searchEndValue } = getActiveDateRangeValues(activeQueryParams, filter.key);
      component = (
        <DateRangeMC
          key={key}
          handleChange={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultDate={{
            startDate: searchStartValue,
            endDate: searchEndValue
          }}
          label={filter.label}
        />
      );
    }
      break;
    case 'async-paginated-select':
      component = (
        <AsyncPaginatedSelectMC
          key={key || filter.key}
          label={filter.label}
          loadOptions={filter.loadOptions}
          onSelect={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultValue={activeQueryParams[filter.key]}
          placeholder={filter.placeholder}
          useFloatingLabel={useFloatingLabel}
        />
      );
      break;
    case 'async-paginated-multi-select':
      component = (
        <AsyncPaginatedMultiSelectMC
          key={key}
          name={filter.key}
          label={filter.label}
          loadOptions={filter.loadOptions}
          onSelect={(_, value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultValue={formatMultiIds(activeQueryParams[filter.key])}
          placeholder={filter.placeholder}
          maxSelected={filter.maxSelected}
          useFloatingLabel={useFloatingLabel}
        />
      );
      break;
    case 'amount-comparison':
      component = (
        <AmountComparisonFilterMC
          key={key}
          currency={filter.currency || 'USD'}
          defaultAmount={activeQueryParams[filter.key] || ''}
          defaultComparison={activeQueryParams[`${filter.key}_comparison`] || 'greater_than'}
          onChange={(value) => navigateToFiler(filter, value, navigate, searchParams)}
        />
      );
      break;
    case 'multi-select-group':
      component = (
        <MultiSelectDropdownGroupMC
          key={key || filter.key}
          label={filter.label}
          options={filter.options || []}
          name={filter.key}
          onChange={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultValue={activeQueryParams[filter.key]}
          placeholder={filter.placeholder}
          search={filter.search !== undefined ? filter.search : true}
        />
      );
      break;
    case 'duration-comparison':
      component = (
        <DurationComparisonInputMC
          key={key || filter.key}
          onChange={(value) => navigateToFiler(filter, value, navigate, searchParams)}
          defaultHours={activeQueryParams[`${filter.key}_hours`] || ''}
          defaultMinutes={activeQueryParams[`${filter.key}_minutes`] || ''}
          defaultSeconds={activeQueryParams[`${filter.key}_seconds`] || ''}
          defaultComparison={activeQueryParams[`${filter.key}_comparison`] || 'greater_than'}
        />
      );
      break;
    default:
      break;
  }
  return (
    <div key={key} className={`tw-flex tw-flex-col tw-align-middle tw-justify-center ${filter.containerClassName || ''}`}>
      {component}
    </div>
  );
};
